import { Photo, Today as TodayIcon, Visibility, Warning as WarningIcon } from '@mui/icons-material';
import { Box, SxProps, TableCell, TableRow, Tooltip } from '@mui/material';
import CycleStatusBadge from './CycleStatusBadge';
import { useStore } from '@priolo/jon';
import { TAGS } from 'components/planner/types';
import RefCmp from 'components/RefCmp';
import LostTrolley from 'images/lostTrolley';
import React, { FunctionComponent, useMemo } from 'react';
import cycleSo from 'stores/cycle';
import cycleDialogSo, { CYCLE_DIALOG_TAB, CYCLE_EDIT_TYPE } from 'stores/cycle/editDialog';
import { CYCLE_STATUS } from 'stores/cycle/types';
import { getCropNames, getCycleStatus, getLayersInHarvest } from 'stores/cycle/utils/cycle';
import { EditCycle } from 'stores/cycle/utils/edit';
import { SESSION_TYPE } from "stores/draft/type";
import farmSo from 'stores/farm';
import { getGrowUnitName } from 'stores/growUnit/utils';
import incidentSo from 'stores/incidents';
import querySo from "stores/route/query";
import { URL_PAR } from 'stores/route/utils/types';
import theme from 'styles/theme';
import { Cycle } from 'types/Cycle';
import { isToday } from 'utils';
import { getSxRow } from 'utils/entity';
import { dateFormat } from 'utils/humanize';
import dayjs from 'dayjs';



interface Props {
    cycle: Cycle
}

const CyclesRow: FunctionComponent<Props> = ({
    cycle,
}) => {

    // STORE
    useStore(cycleSo)
    useStore(incidentSo)
    const querySa = useStore(querySo)

    // HOOKs
    const cycleUuidSel = useMemo(() => querySo.getSearchUrl(URL_PAR.CYCLE_SEL), [querySa.queryUrl])

    // HANDELEs
    // click editazione di un CYCLEs
    const handleDClickRow = (e: React.MouseEvent, cycle: Cycle) => {
        e.stopPropagation()
        openDialog(cycle)
    }
    // seleziono la riga
    const handleClickRow = async (e: React.MouseEvent, cycle: Cycle) => {
        e.stopPropagation()
        querySo.setSearchUrl([URL_PAR.CYCLE_SEL, cycle.cycleUuid])
    }
    const handlePhotoClick = (e: React.MouseEvent) => {
        e.stopPropagation()
        openDialog(cycle, CYCLE_DIALOG_TAB.PHOTOS)
    }
    const handleLostTrollyClick = (e: React.MouseEvent) => {
        e.stopPropagation()
        openDialog(cycle, CYCLE_DIALOG_TAB.LOST_TROLLEY)
    }

    // UTILS
    const openDialog = async (cycle: Cycle, tab: CYCLE_DIALOG_TAB = CYCLE_DIALOG_TAB.DETAIL) => {
        const action = await cycleDialogSo.open({ cycle, tab })
        if (action == CYCLE_EDIT_TYPE.REJECT) {
            //querySo.updateQueryUrl()
        } else if (action == CYCLE_EDIT_TYPE.HARVEST) {
            await cycleSo.fetch({ force: true })
            //querySo.updateQueryUrl()
        }
    }

    // RENDER
    const sxRow = useMemo(() => getSxRow(cycle._edit?.type), [cycle._edit?.type])
    const guName = useMemo(() => getGrowUnitName(farmSo.getGrowUnit(cycle.growUnitId)), [cycle, farmSo.state.select])
    const photosNum = cycle.numberOfAttachments ?? 0

    const isSelect = cycle.cycleUuid == cycleUuidSel
    const isPreview = EditCycle.getExtra(cycle, TAGS.SESSION) == SESSION_TYPE.PREVIEW
    const unitYield = isNaN(cycle.unitsYield) || cycle.unitsYield == null ? "--" : cycle.unitsYield
    const cycleStatus = getCycleStatus(cycle)
    const plannedYield = cycle.plannedYield ? (+cycle.plannedYield).toFixed(2) : "--"
    const realYield = cycle.yield ? (+cycle.yield).toFixed(2) : "--"
    const haveWarn = cycle.layers?.some(layer => layer["errors"]?.length > 0) ?? false
    const [harvestingDate, harvestingDiscrepance] = useMemo(() => {
        if (!!cycle.yieldDate && !!cycle.harvestingDate && dayjs(cycle.harvestingDate).add(1, "day").diff(dayjs(cycle.yieldDate), "day") != 0) {
            return [cycle.yieldDate, true]
        }
        return [cycle.harvestingDate, false]
    }, [cycle.harvestingDate, cycle.yieldDate]);

    return <TableRow hover
        sx={{ cursor: "pointer" }}
        onDoubleClick={e => handleDClickRow(e, cycle)}
        onClick={e => handleClickRow(e, cycle)}
        selected={isSelect}
    >
        <TableCell scope="cycle" sx={sxRow}>
            <Box sx={{ display: "flex" }}>
                <RefCmp uuid={cycle.cycleUuid} />
                {haveWarn &&
                    <WarningIcon color="secondary" fontSize="small" sx={sxIcon} />
                }
                {isPreview && <Visibility sx={{ ml: 1 }} />}
            </Box>
        </TableCell>
        <TableCell sx={sxRow}>
            {guName}
        </TableCell>
        <TableCell sx={sxRow}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {cycle.numberOfIncidents > 0 && (
                    <Tooltip title={`${cycle.numberOfIncidents} INCIDENT${cycle.numberOfIncidents > 1 ? "S" : ""}`}><Box sx={{ display: "flex" }}>
                        <LostTrolley style={{ color: theme.palette.secondary.main }}
                            onClick={handleLostTrollyClick}
                        />
                    </Box></Tooltip>
                )}
                {photosNum > 0 && (
                    <Tooltip title={`${photosNum} PHOTO${photosNum > 1 ? "S" : ""}`}><Box sx={{ display: "flex" }}>
                        <Photo style={{ color: theme.palette.primary.main }}
                            onClick={handlePhotoClick}
                        />
                    </Box></Tooltip>
                )}
                {getCropNames(cycle)}
            </Box>
        </TableCell>
        <TableCell align="center" sx={[{}, sxRow]}>
            <CycleStatusBadge cycleStatus={cycleStatus} />
        </TableCell>
        <TableCell sx={sxRow}>
            <RowDateToday date={cycle.seedingDate} />
        </TableCell>
        <TableCell sx={sxRow}>
            <Box sx={{ display: "flex" }}>
                <RowDateToday date={harvestingDate} />
                {harvestingDiscrepance &&
                    <Tooltip title={`OFF-SCHEDULE HARVEST. PLANNED: ${cycle.harvestingDate}`}>
                        <WarningIcon color="secondary" fontSize="small" sx={sxIcon} />
                    </Tooltip>
                }
            </Box>
        </TableCell>
        <TableCell align="center" sx={sxRow}>
            {getLayersInHarvest(cycle)}
        </TableCell>
        <TableCell align="right" sx={sxRow}>
            {plannedYield}
        </TableCell>
        <TableCell align="right" sx={sxRow}>
            {realYield}
        </TableCell>
        <TableCell align="right" sx={sxRow}>
            {unitYield}
        </TableCell>
    </TableRow>
}

export default CyclesRow

const RowDateToday = ({ date }: { date: string }) => {

    const dateStr = dateFormat(date)
    const today = isToday(date)

    return (
        <Box sx={{ display: "flex" }}>
            {dateStr}
            {today && (
                <Tooltip title="TODAY">
                    <TodayIcon fontSize="small" sx={sxIcon} />
                </Tooltip>
            )}
        </Box>
    )
}

const sxIcon: SxProps = {
    marginLeft: "5px",
    marginRight: "-20px"
}
